import { Reveal } from "@/lib/motion";
import { Info } from "lucide-react";
import { Link } from "react-router-dom";
import { TRUSTED_LOGOS, LOGO_KINDS, resolveLogoKind } from "@/data/trustedLogos";

/**
 * Audit remediation (critical trust fix):
 *   Every brand logo on the homepage carries a visible kind chip —
 *   Live client · Pilot · Partner · Illustrative — so visitors never
 *   confuse these with verified client relationships.
 *   Mapping lives in /app/frontend/src/data/trustedLogos.js; flipping a
 *   kind requires zero component changes.
 */
export default function TrustedLogos() {
  const liveCount = TRUSTED_LOGOS.filter((l) => resolveLogoKind(l) === "live").length;
  const partnerCount = TRUSTED_LOGOS.filter((l) => resolveLogoKind(l) === "partner").length;
  return (
    <section
      className="relative z-10 py-12 bg-white border-t border-slate-100"
      data-testid="trusted-logos"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal className="text-center">
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500">
            {liveCount > 0 || partnerCount > 0
              ? "Platforms, pilots & partners"
              : "Illustrative brand showcases"}
          </p>
          <p
            className="mt-1 text-[11px] text-slate-400 inline-flex items-center gap-1"
            data-testid="trusted-logos-disclosure"
          >
            <Info size={11} />
            {liveCount > 0 || partnerCount > 0 ? (
              <>Every logo is labelled so you can tell live clients, pilots, partners and illustrative examples apart. See{" "}
                <Link to="/case-studies" className="underline hover:text-brand">verified case studies</Link>.</>
            ) : (
              <>Sample layouts only — not verified clients. See{" "}
                <Link to="/case-studies" className="underline hover:text-brand">verified case studies</Link>.</>
            )}
          </p>
        </Reveal>
        <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-6 mt-6 opacity-80">
          {TRUSTED_LOGOS.map((l, i) => {
            const kind = resolveLogoKind(l);
            const meta = LOGO_KINDS[kind] || LOGO_KINDS.illustrative;
            return (
              <Reveal key={l.name} delay={i * 0.05}>
                <div
                  className="relative flex items-center gap-2 text-slate-400 hover:text-slate-600 transition-colors"
                  data-testid={`trusted-logo-${l.name.toLowerCase()}`}
                >
                  <l.icon size={22} />
                  <span className="font-heading font-bold text-lg tracking-tight">{l.name}</span>
                  <span
                    data-testid={`trusted-logo-kind-${l.name.toLowerCase()}`}
                    className={`text-[9px] font-bold uppercase tracking-wider border px-1.5 py-0.5 rounded-full ${meta.bg}`}
                  >
                    {meta.label}
                  </span>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}
